import { useUserRole } from '@/hooks/useUserRole'
import { showSuccess, showValidationMessage, formatApiError } from '@/lib/utils/notifications'
import type { UserRole, UseUserManagementEditRolesParams } from '../types'
import { removeRole, setFinanceManager, updateSalesRole } from '../services/userManagementApi'
import { isLibertyRoleName, isProtectedRoleName } from '../utils/editPageUi'

export function useUserManagementEditRoles({
  userId,
  user,
  fetchUser,
  isAdmin,
  canRemoveRole,
  canImpersonateApplicant,
  canImpersonatePmc,
  canSetFinMgr,
  removingRoleId,
  setRemovingRoleId,
  finMgrRoleId,
  setFinMgrRoleId,
  pendingSalesRole,
  setPendingSalesRole,
  setConfirmOpen,
}: UseUserManagementEditRolesParams) {
  const handleRemoveRole = async () => {
    if (!userId || removingRoleId == null) return
    try {
      await removeRole(userId, removingRoleId)
      showSuccess('User Role Removed Successfully!')
      void fetchUser()
    } catch (err) {
      showValidationMessage(formatApiError(err), 'Error')
    } finally {
      setRemovingRoleId(null)
      setConfirmOpen(null)
    }
  }

  const handleFinanceManager = async () => {
    if (!userId || finMgrRoleId == null || !user) return
    const targetRole = user.roles.find((role) => role.role_id === finMgrRoleId)
    const newValue = targetRole?.fin_mgr_attribute !== 'YES'
    try {
      await setFinanceManager(userId, { fin_mgr_attribute: newValue, role_id: finMgrRoleId })
      showSuccess('Role Setting Changed Successfully')
      void fetchUser()
    } catch (err) {
      showValidationMessage(formatApiError(err), 'Error')
    } finally {
      setFinMgrRoleId(null)
      setConfirmOpen(null)
    }
  }

  const handleSalesRoleUpdate = async () => {
    if (!userId || !pendingSalesRole) return
    try {
      const result = await updateSalesRole(userId, pendingSalesRole)
      void fetchUser()
      if (result === 'is_assigned') {
        setConfirmOpen('sales-role-assigned')
      } else {
        setConfirmOpen('sales-role-unassigned')
      }
    } catch (err) {
      showValidationMessage(formatApiError(err), 'Error')
      setPendingSalesRole('')
      setConfirmOpen(null)
    }
  }

  const canImpersonateRole = (role: UserRole): boolean => {
    if (isLibertyRoleName(role.role_name)) return false
    if (role.role_name === 'ROLE_RENTER') return canImpersonateApplicant
    return canImpersonatePmc
  }

  const canRemoveRoleItem = (role: UserRole): boolean => {
    if (!canRemoveRole) return false
    if (isProtectedRoleName(role.role_name)) return isAdmin
    return true
  }

  const { currentRole } = useUserRole()

  const canShowFinMgr = (role: UserRole): boolean => {
    if (role.role_name !== 'ROLE_PROPERTY_MANAGER') return false
    if (canSetFinMgr) return true
    return currentRole?.role_name === 'ROLE_COMPANY_ADMIN' && currentRole?.fin_mgr_attribute === 'YES'
  }

  const requestRemoveRole = (roleId: number, onlyOne: boolean) => {
    setRemovingRoleId(roleId)
    setConfirmOpen(onlyOne ? 'remove-role-only-one' : 'remove-role')
  }

  const requestFinanceManager = (roleId: number) => {
    setFinMgrRoleId(roleId)
    setConfirmOpen('fin-mgr')
  }

  const requestSalesRole = (_roleId: number, salesRole: string) => {
    setPendingSalesRole(salesRole)
    setConfirmOpen('sales-role')
  }

  return {
    handleRemoveRole,
    handleFinanceManager,
    handleSalesRoleUpdate,
    canImpersonateRole,
    canRemoveRoleItem,
    canShowFinMgr,
    requestRemoveRole,
    requestFinanceManager,
    requestSalesRole,
  }
}
